import React, { useState } from "react";
import {
  Globe,
  RefreshCw,
  Loader,
  CheckCircle,
  AlertCircle,
  Download,
  X,
} from "lucide-react";
import { SitemapService } from "../services/sitemapService";
import SEO from "./SEO";

interface SitemapResult {
  success: boolean;
  message: string;
  stats?: {
    totalPosts: number;
    optimizedPosts: number;
    recentPosts: number;
    lastOptimized: string | null;
    totalPrompts: number;
    totalPages: number;
    seoPagesOptimized: number;
  };
  sitemapContent?: string;
}

interface ContentItem {
  title: string;
  slug: string;
  seo_optimized_at?: string | null;
  updated_at: string;
}

export default function GenerateSitemap() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<SitemapResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPagesModal, setShowPagesModal] = useState(false);
  const [showPostsModal, setShowPostsModal] = useState(false);
  const [pagesData, setPagesData] = useState<{
    optimized: ContentItem[];
    toOptimize: ContentItem[];
  }>({ optimized: [], toOptimize: [] });
  const [postsData, setPostsData] = useState<{
    optimized: ContentItem[];
    toOptimize: ContentItem[];
  }>({ optimized: [], toOptimize: [] });

  const handleGenerateSitemap = async () => {
    try {
      setIsGenerating(true);
      setError(null);
      setResult(null);

      console.log("🚀 Starting sitemap generation...");

      // Trigger sitemap regeneration
      const regenerationResult =
        await SitemapService.triggerSitemapRegeneration();

      if (!regenerationResult.success) {
        throw new Error(
          regenerationResult.message || "Failed to generate sitemap",
        );
      }

      // Get sitemap stats
      const stats = await SitemapService.getSitemapStats();

      // Get the actual sitemap content
      const sitemapResponse = await fetch("/sitemap.xml");
      const sitemapContent = await sitemapResponse.text();

      const result: SitemapResult = {
        success: true,
        message: "Sitemap generated successfully!",
        stats: {
          totalPosts: stats.totalPosts || 0,
          optimizedPosts: stats.optimizedPosts || 0,
          recentPosts: stats.recentPosts || 0,
          lastOptimized: stats.lastOptimized,
          totalPrompts: stats.totalPrompts || 0,
          totalPages: stats.totalPages || 0,
          seoPagesOptimized: stats.seoPagesOptimized || 0,
        },
        sitemapContent,
      };

      setResult(result);
      console.log("✅ Sitemap generation completed successfully");
    } catch (err) {
      console.error("❌ Sitemap generation failed:", err);
      setError(
        err instanceof Error ? err.message : "Failed to generate sitemap",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadSitemap = () => {
    if (result?.sitemapContent) {
      const blob = new Blob([result.sitemapContent], {
        type: "application/xml",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sitemap.xml";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handlePagesClick = async () => {
    try {
      // Define static pages (not posts or prompts) with their actual SEO status
      const staticPages = [
        {
          title: "Landing Page",
          slug: "/",
          type: "page",
          seo_optimized_at: "2025-01-07T00:00:00.000Z", // ✅ Original optimization
          updated_at: new Date().toISOString(),
        },
        {
          title: "Courses",
          slug: "/courses",
          type: "page",
          seo_optimized_at: "2025-01-07T00:00:00.000Z", // ✅ Original optimization
          updated_at: new Date().toISOString(),
        },
        {
          title: "Fractional PM",
          slug: "/fractional-pm",
          type: "page",
          seo_optimized_at: "2025-01-07T00:00:00.000Z", // ✅ Original optimization
          updated_at: new Date().toISOString(),
        },
        {
          title: "Blog",
          slug: "/blog",
          type: "page",
          seo_optimized_at: "2025-08-08T00:00:00.000Z", // ✅ Recently optimized
          updated_at: new Date().toISOString(),
        },
        {
          title: "About",
          slug: "/about",
          type: "page",
          seo_optimized_at: "2025-01-07T00:00:00.000Z", // ✅ Original optimization
          updated_at: new Date().toISOString(),
        },
        {
          title: "Prompt Library",
          slug: "/prompt-library",
          type: "page",
          seo_optimized_at: "2025-01-07T00:00:00.000Z", // ✅ Original optimization
          updated_at: new Date().toISOString(),
        },
        {
          title: "Contact",
          slug: "/contact",
          type: "page",
          seo_optimized_at: "2025-08-08T00:00:00.000Z", // ✅ Recently optimized
          updated_at: new Date().toISOString(),
        },
        {
          title: "Privacy Policy",
          slug: "/privacy",
          type: "page",
          seo_optimized_at: "2025-08-08T00:00:00.000Z", // ✅ Recently optimized
          updated_at: new Date().toISOString(),
        },
        {
          title: "Terms of Service",
          slug: "/terms",
          type: "page",
          seo_optimized_at: "2025-08-08T00:00:00.000Z", // ✅ Recently optimized
          updated_at: new Date().toISOString(),
        },
      ];

      // Filter based on actual SEO optimization status
      const optimized = staticPages.filter((page) => page.seo_optimized_at);
      const toOptimize = staticPages.filter((page) => !page.seo_optimized_at);

      setPagesData({ optimized, toOptimize });
      setShowPagesModal(true);
    } catch (error) {
      console.error("Error fetching pages data:", error);
    }
  };

  const handlePostsClick = async () => {
    try {
      const { posts } = await SitemapService.getDetailedContentData();

      const optimized = posts.filter((post: any) => post.seo_optimized_at);
      const toOptimize = posts.filter((post: any) => !post.seo_optimized_at);

      setPostsData({ optimized, toOptimize });
      setShowPostsModal(true);
    } catch (error) {
      console.error("Error fetching posts data:", error);
    }
  };

  return (
    <div className="space-y-6">
      <SEO
        title="One80Labs - Generate Sitemap"
        description="Generate and manage the website sitemap for One80Labs."
        noindex={true}
      />

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-6">
            <Globe className="h-8 w-8 text-[#F98B3D] mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Generate Sitemap
              </h2>
              <p className="text-gray-600">
                Generate a comprehensive sitemap for all published content
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Generate Button */}
            <div className="flex items-center space-x-4">
              <button
                onClick={handleGenerateSitemap}
                disabled={isGenerating}
                className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
              >
                {isGenerating ? (
                  <>
                    <Loader className="h-5 w-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-5 w-5 mr-2" />
                    Generate Sitemap
                  </>
                )}
              </button>

              {result?.success && (
                <button
                  onClick={handleDownloadSitemap}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download XML
                </button>
              )}
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Success Result */}
            {result?.success && (
              <div className="space-y-6">
                <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-md">
                  <div className="flex">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                    <div className="ml-3">
                      <p className="text-green-700 font-medium">
                        {result.message}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                {result.stats && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Sitemap Statistics
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#F98B3D]">
                          {result.stats.totalPages}
                        </div>
                        <div className="text-sm text-gray-600">Total Pages</div>
                      </div>
                      <div
                        className="text-center cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors"
                        onClick={handlePagesClick}
                      >
                        <div className="text-2xl font-bold text-[#F98B3D]">
                          {result.stats.seoPagesOptimized}
                        </div>
                        <div className="text-sm text-gray-600">
                          SEO Pages Optimized
                        </div>
                        <div className="text-xs text-blue-600 mt-1">
                          Click to view details
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#F98B3D]">
                          {result.stats.totalPosts}
                        </div>
                        <div className="text-sm text-gray-600">Total Posts</div>
                      </div>
                      <div
                        className="text-center cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors"
                        onClick={handlePostsClick}
                      >
                        <div className="text-2xl font-bold text-[#F98B3D]">
                          {result.stats.optimizedPosts}
                        </div>
                        <div className="text-sm text-gray-600">
                          SEO Posts Optimized
                        </div>
                        <div className="text-xs text-blue-600 mt-1">
                          Click to view details
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#F98B3D]">
                          {result.stats.totalPrompts}
                        </div>
                        <div className="text-sm text-gray-600">
                          Total Prompts
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#F98B3D]">
                          {result.stats.lastOptimized ? "Yes" : "No"}
                        </div>
                        <div className="text-sm text-gray-600">
                          Last Optimized
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 text-sm text-gray-500">
                      Last updated:{" "}
                      {new Date(
                        result.stats.lastOptimized || "",
                      ).toLocaleString()}
                    </div>
                  </div>
                )}

                {/* Sitemap Preview */}
                {result.sitemapContent && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Sitemap Preview
                    </h3>
                    <div className="bg-white border rounded-lg p-4 max-h-96 overflow-y-auto">
                      <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                        {result.sitemapContent}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Pages Modal */}
            {showPagesModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">
                      Pages SEO Status
                    </h3>
                    <button
                      onClick={() => setShowPagesModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-lg font-semibold text-green-700 mb-3">
                        ✅ Optimized Pages ({pagesData.optimized.length})
                      </h4>
                      <div className="space-y-2">
                        {pagesData.optimized.map((item, index) => (
                          <div
                            key={index}
                            className="bg-green-50 p-3 rounded border border-green-200"
                          >
                            <div className="font-medium text-green-800">
                              {item.title}
                            </div>
                            <div className="text-sm text-green-600">
                              /{item.slug}
                            </div>
                            <div className="text-xs text-green-500">
                              Optimized:{" "}
                              {new Date(
                                item.seo_optimized_at || "",
                              ).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                        {pagesData.optimized.length === 0 && (
                          <div className="text-gray-500 text-center py-4">
                            No optimized pages found
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold text-orange-700 mb-3">
                        ⚠️ Pages to Optimize ({pagesData.toOptimize.length})
                      </h4>
                      <div className="space-y-2">
                        {pagesData.toOptimize.map((item, index) => (
                          <div
                            key={index}
                            className="bg-orange-50 p-3 rounded border border-orange-200"
                          >
                            <div className="font-medium text-orange-800">
                              {item.title}
                            </div>
                            <div className="text-sm text-orange-600">
                              /{item.slug}
                            </div>
                            <div className="text-xs text-orange-500">
                              Last updated:{" "}
                              {new Date(item.updated_at).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                        {pagesData.toOptimize.length === 0 && (
                          <div className="text-gray-500 text-center py-4">
                            All pages are optimized!
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Posts Modal */}
            {showPostsModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">
                      Posts SEO Status
                    </h3>
                    <button
                      onClick={() => setShowPostsModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-lg font-semibold text-green-700 mb-3">
                        ✅ Optimized Posts ({postsData.optimized.length})
                      </h4>
                      <div className="space-y-2">
                        {postsData.optimized.map((item, index) => (
                          <div
                            key={index}
                            className="bg-green-50 p-3 rounded border border-green-200"
                          >
                            <div className="font-medium text-green-800">
                              {item.title}
                            </div>
                            <div className="text-sm text-green-600">
                              /{item.slug}
                            </div>
                            <div className="text-xs text-green-500">
                              Optimized:{" "}
                              {new Date(
                                item.seo_optimized_at || "",
                              ).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                        {postsData.optimized.length === 0 && (
                          <div className="text-gray-500 text-center py-4">
                            No optimized posts found
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold text-orange-700 mb-3">
                        ⚠️ Posts to Optimize ({postsData.toOptimize.length})
                      </h4>
                      <div className="space-y-2">
                        {postsData.toOptimize.map((item, index) => (
                          <div
                            key={index}
                            className="bg-orange-50 p-3 rounded border border-orange-200"
                          >
                            <div className="font-medium text-orange-800">
                              {item.title}
                            </div>
                            <div className="text-sm text-orange-600">
                              /{item.slug}
                            </div>
                            <div className="text-xs text-orange-500">
                              Last updated:{" "}
                              {new Date(item.updated_at).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                        {postsData.toOptimize.length === 0 && (
                          <div className="text-gray-500 text-center py-4">
                            All posts are optimized!
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
